<template>
  <div class="p-m-3 sp-page">
    <MockBanner />
    <h2>Duplicate Projects</h2>
    <p class="sp-sub">
      Projects or Sites entered twice, and the same customer quoted twice on one project. New ones are caught when entered; this page is the clean-up
      list and the regular spot check. Merging is for Managers and Admin.
    </p>

    <div class="p-tabview p-component">
      <ul class="p-tabview-nav" role="tablist">
        <li v-for="tab in tabs" :key="tab.key" role="presentation" :class="{ 'p-highlight': tab.key === active }">
          <a class="p-tabview-nav-link" role="tab" @click="active = tab.key">
            <span class="p-tabview-title">{{ tab.label }} ({{ rowsFor(tab.key).length }})</span>
          </a>
        </li>
      </ul>
    </div>

    <div class="p-card p-p-3 p-mt-3 p-mb-3">
      <div class="sp-note grey" style="margin: 0;">
        <i class="pi pi-cog"></i>
        <span v-if="active === 'projects'">
          <strong>How projects are matched</strong> (better than today's enquiry check): same Site, plus similar names by word overlap (not whole-string
          similarity, so "NANOSCIENCE EPITAXY UNIT" and "NANOSCIENCE AND TECHNOLOGY CENTRE" are compared word by word), a project named after a product type,
          customers in common, values in common and created dates close together. Each pair is scored High / Medium / Low with its reasons in words.
        </span>
        <span v-else-if="active === 'sites'">
          <strong>How sites are matched:</strong> the same full postcode, or the same postcode area plus a similar name (word overlap after dropping words like
          LTD, THE, SITE), plus customers in common. Partial postcodes such as "M" or "-" are not used as evidence.
        </span>
        <span v-else>
          <strong>S2:</strong> the same customer and the same product type on one project, with two live enquiries (usually from two branches). Only one counts
          (setting: the <strong>{{ s2RuleText }}</strong>). On the TEST data, 14 live pairs have the same customer at two branches, and only
          <strong>2</strong> have the same product type. The other 12 are different product types (S3, e.g. Ironmongery from MIDLANDS and Doorsets from
          DOORSETS) and both count.
        </span>
      </div>
    </div>

    <div class="p-card p-p-3">
      <DataTable
        :value="rowsFor(active)"
        :autoLayout="true"
        class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
        :rowHover="true"
        selectionMode="single"
        :selection.sync="selected"
        dataKey="id"
        @row-click="dialog = true"
      >
        <Column v-for="c in columns" :key="c.field" :field="c.field" :header="c.header" :sortable="true">
          <template #body="s">
            <span v-if="c.chip" :class="'sp-chip ' + s.data[c.field]">{{ s.data[c.field] }}</span>
            <div v-else-if="c.money" class="sp-money">{{ money(s.data[c.field]) }}</div>
            <span v-else>{{ s.data[c.field] }}</span>
          </template>
        </Column>
      </DataTable>
    </div>

    <Dialog v-if="selected" :visible.sync="dialog" :modal="true" :header="selected.title" :style="{ width: '820px' }">
      <h4 style="margin-top: 0;">{{ selected.reason }} <span :class="'sp-chip ' + selected.confidence">{{ selected.confidence }}</span></h4>
      <ul style="margin-top: 0;"><li v-for="(p, i) in selected.proof" :key="i">{{ p }}</li></ul>
      <table class="sp-table">
        <thead><tr><th></th><th>{{ selected.leftTitle }}</th><th>{{ selected.rightTitle }}</th></tr></thead>
        <tbody>
          <tr v-for="f in selected.compare" :key="f[0]">
            <th>{{ f[0] }}</th>
            <td>{{ f[1] }}</td>
            <td>{{ f[2] }} <i v-if="f[1] === f[2]" class="pi pi-check" style="color: #2e7d32;" title="matches"></i></td>
          </tr>
        </tbody>
      </table>
      <p v-if="selected.suggest" class="p-mt-3"><strong>Suggested:</strong> {{ selected.suggest }}</p>
      <template #footer>
        <Button label="Not a duplicate" icon="pi pi-times" class="p-button-outlined" style="width: auto;" disabled />
        <Button :label="selected.action" icon="pi pi-sitemap" class="p-button-warning" style="width: auto;" disabled />
      </template>
    </Dialog>
  </div>
</template>

<script>
import MockBanner from './MockBanner.vue'
import { settings, money } from './mockData'

const ROWS = {
  projects: [
    {
      id: 'p1', site: 'GREY MARE LANE', town: 'Manchester', left: 'SCREENS', right: 'MAIN SCHEME', confidence: 'High',
      reason: 'Same job: "SCREENS" is a product type, not a project', leftEnq: 1, rightEnq: 8, days: 4, value: 13585,
      title: 'GREY MARE LANE: SCREENS vs MAIN SCHEME', leftTitle: 'SCREENS', rightTitle: 'MAIN SCHEME',
      proof: [
        'Same site, Grey Mare Lane, Manchester.',
        'The project name is a product type (Screens).',
        'RJC Joinery quotes Doorsets on MAIN SCHEME and Screens on SCREENS.',
        'The screens quote on SCREENS (£13,585) is the same price as the three screens quotes on MAIN SCHEME, entered 4 days apart.'
      ],
      compare: [['Site', 'GREY MARE LANE', 'GREY MARE LANE'], ['Product types', 'Screens', 'Doorsets - STD, Screens'], ['Customers', 'RJC JOINERY LTD', 'RJC, JDM, L N JOINERY, FABER'], ['Screens value', '£13,585', '£13,585'], ['Created', '24/07/2026', '29/05/2025']],
      suggest: 'Merge SCREENS into MAIN SCHEME. Screens then has 4 contractors quoting and counts once, at £13,585.', action: 'Merge into MAIN SCHEME'
    },
    {
      id: 'p2', site: 'UNIVERSITY OF SHEFFIELD', town: 'Sheffield', left: 'SITE-WIDE', right: 'ALFRED DENNY BUILDING', confidence: 'Medium',
      reason: 'An enquiry with no building named, on a site with several buildings', leftEnq: 1, rightEnq: 1, days: 57, value: 4911,
      title: 'UNIVERSITY OF SHEFFIELD: SITE-WIDE vs ALFRED DENNY BUILDING', leftTitle: 'SITE-WIDE', rightTitle: 'ALFRED DENNY BUILDING',
      proof: ['Same site.', 'SITE-WIDE holds one lost enquiry (Vision Joinery, Doorsets - BESPOKE) with no building in its description.', 'Could belong to any of the 3 buildings: check the quote.'],
      compare: [['Site', 'UNIVERSITY OF SHEFFIELD', 'UNIVERSITY OF SHEFFIELD'], ['Product types', 'Doorsets - BESPOKE', 'Doorsets - STD'], ['Customers', 'VISION JOINERY LTD', 'DOLPHIN (SHEFFIELD) LIMITED'], ['Postcode', 'S10 2TN', 'S10 2TN'], ['Created', '02/03/2026', '28/04/2026']],
      suggest: 'Check the quote, then move the enquiry to the right building.', action: 'Move enquiry to ALFRED DENNY BUILDING'
    },
    {
      id: 'p3', site: 'UNIVERSITY OF SHEFFIELD', town: 'Sheffield', left: 'NANOSCIENCE EPITAXY UNIT', right: 'NANOSCIENCE AND TECHNOLOGY CENTRE', confidence: 'Low',
      reason: 'Similar names, but different postcodes and products', leftEnq: 1, rightEnq: 3, days: 17, value: 415,
      title: 'UNIVERSITY OF SHEFFIELD: two NANOSCIENCE projects', leftTitle: 'NANOSCIENCE EPITAXY UNIT', rightTitle: 'NANOSCIENCE AND TECHNOLOGY CENTRE',
      proof: ['Same site; one word in common (NANOSCIENCE).', 'Different postcodes: S10 2N and S3 7HQ.', 'James Burrell quotes on both: Ironmongery on one, Doorsets on the other.'],
      compare: [['Postcode', 'S10 2N', 'S3 7HQ'], ['Product types', 'Ironmongery', 'Doorsets - STD'], ['Customers', 'JAMES BURRELL LTD', 'JAMES BURRELL, JEWSON, CONSTRUCTION SERVICES (MANSFIELD)'], ['Created', '15/05/2026', '01/06/2026']],
      suggest: 'Probably two buildings. Mark "Not a duplicate" so it is not shown again.', action: 'Merge'
    },
    {
      id: 'p4', site: 'TRAFFORD WATERS', town: 'Manchester', left: 'PARCEL 3', right: 'PARCEL 2', confidence: 'Low',
      reason: 'Same site, different parcel numbers', leftEnq: 4, rightEnq: 2, days: 440, value: 450731,
      title: 'TRAFFORD WATERS: PARCEL 3 vs PARCEL 2', leftTitle: 'PARCEL 3', rightTitle: 'PARCEL 2',
      proof: ['Same site; the names differ only by number.', 'Different prices (£450,731 vs £106,535–£125,690), 14 months apart.', 'Lloyd Worrall quotes on both.'],
      compare: [['Product types', 'Doorsets - STD', 'Doorsets - STD'], ['Customers', 'WEST END, L N JOINERY, LLOYD WORRALL, T M J', 'ROWLINSON, LLOYD WORRALL'], ['Created', '07/04/2026', '22/01/2025']],
      suggest: 'Different parcels. Not a duplicate.', action: 'Merge'
    }
  ],
  sites: [
    {
      id: 's1', site: 'BRIGHT HORIZONS WATERWAYS NURSERY', town: 'Oxford', left: 'BRIGHT HORIZONS WATERWAYS NURSERY', right: 'WATERWAYS NURSERY', confidence: 'High',
      reason: 'Same full postcode, one name inside the other, same customer', leftEnq: 1, rightEnq: 1, days: 2, value: 4046,
      title: 'Sites: BRIGHT HORIZONS WATERWAYS NURSERY vs WATERWAYS NURSERY', leftTitle: 'BRIGHT HORIZONS WATERWAYS NURSERY', rightTitle: 'WATERWAYS NURSERY',
      proof: ['Same full postcode OX2 7NL.', '"WATERWAYS NURSERY" is inside the other name (Bright Horizons is the operator).', 'Stepnell Limited quotes on both, 2 days apart: Ironmongery from MIDLANDS, Doorsets from DOORSETS.'],
      compare: [['Postcode', 'OX2 7NL', 'OX2 7NL'], ['Street', '—', 'Clearwater Place'], ['Town', 'Oxford', 'Oxford'], ['Customers', 'STEPNELL LIMITED', 'STEPNELL LIMITED'], ['Created', '31/07/2026', '29/07/2026']],
      suggest: 'Merge into WATERWAYS NURSERY (it has the street). The two NURSERY projects then merge too, and the job shows Doorsets and Ironmongery, each counted.', action: 'Merge into WATERWAYS NURSERY'
    }
  ],
  s2: [
    {
      id: 'd1', site: 'PREMIER INN LITTLEHAMPTON', town: 'Littlehampton', left: 'Q55443 (DOORSETS)', right: 'Q49807 (MIDLANDS)', confidence: 'High',
      reason: 'CARTER LAUREN, Doorsets - BESPOKE, quoted by two branches', leftEnq: 1, rightEnq: 1, days: 235, value: 56676,
      title: 'S2: CARTER LAUREN at PREMIER INN LITTLEHAMPTON', leftTitle: 'Q55443 (counts: latest)', rightTitle: 'Q49807 (not counted)',
      proof: ['Same project, same customer, same product type.', 'Two branches: DOORSETS £56,676 (07/09/2026) and MIDLANDS £163,314 (15/01/2026).', 'The prices are very different: check that it really is the same scope.'],
      compare: [['Customer', 'CARTER LAUREN', 'CARTER LAUREN'], ['Product type', 'Doorsets - BESPOKE', 'Doorsets - BESPOKE'], ['Branch', 'DOORSETS', 'MIDLANDS'], ['Value', '£56,676', '£163,314'], ['Created', '07/09/2026', '15/01/2026'], ['Status', 'Active', 'Active']],
      suggest: 'Agree with both branches which quote stands, and mark the other Lost or Superseded.', action: 'Keep one, close the other'
    },
    {
      id: 'd2', site: 'BELONG CARE HOME', town: 'Southport', left: 'Q55307 (DOORSETS)', right: 'Q51870 (MIDLANDS)', confidence: 'High',
      reason: 'LLOYD WORRALL DOORSETS, Doorsets - BESPOKE, quoted by two branches', leftEnq: 1, rightEnq: 1, days: 153, value: 1512,
      title: 'S2: LLOYD WORRALL DOORSETS at BELONG CARE HOME', leftTitle: 'Q55307 (counts: latest)', rightTitle: 'Q51870 (not counted)',
      proof: ['Same project, same customer, same product type.', 'Two branches: DOORSETS £1,512 (27/08/2026) and MIDLANDS £469 (27/03/2026).'],
      compare: [['Customer', 'LLOYD WORRALL DOORSETS', 'LLOYD WORRALL DOORSETS'], ['Product type', 'Doorsets - BESPOKE', 'Doorsets - BESPOKE'], ['Branch', 'DOORSETS', 'MIDLANDS'], ['Value', '£1,512', '£469'], ['Created', '27/08/2026', '27/03/2026']],
      suggest: 'Agree which quote stands.', action: 'Keep one, close the other'
    }
  ]
}

export default {
  name: 'SpDuplicateProjects',
  components: { MockBanner },
  data() {
    return {
      tabs: [
        { key: 'projects', label: 'Duplicate projects' },
        { key: 'sites', label: 'Duplicate sites' },
        { key: 's2', label: 'Same customer twice on a project' }
      ],
      active: 'projects',
      selected: null,
      dialog: false
    }
  },
  computed: {
    columns() {
      const left = { projects: 'Project', sites: 'Site', s2: 'Enquiry' }[this.active]
      const right = { projects: 'Possible duplicate of', sites: 'Possible duplicate of', s2: 'Duplicate of' }[this.active]
      return [
        { field: 'site', header: 'Site' },
        { field: 'town', header: 'Town' },
        { field: 'left', header: left },
        { field: 'right', header: right },
        { field: 'reason', header: 'Reason' },
        { field: 'confidence', header: 'Confidence', chip: true },
        { field: 'leftEnq', header: 'Enquiries (left)' },
        { field: 'rightEnq', header: 'Enquiries (right)' },
        { field: 'value', header: 'Value involved £', money: true },
        { field: 'days', header: 'Days apart' }
      ]
    },
    s2RuleText() { return { latest: 'latest one', highest: 'highest one', first: 'first one entered' }[settings.s2Counts] }
  },
  methods: {
    money,
    rowsFor(key) { return ROWS[key] }
  }
}
</script>

<style scoped>
@import './sitesProjects.css';
</style>
